import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import { rootUrl } from '../../../config/config';
import { formatMoney } from '../shared/formatMoney';
import OwnerNav from './OwnerNav';
import { HiExclamationCircle, HiCurrencyDollar, HiTruck } from 'react-icons/hi';

const TYPE_LABELS = {
  MOI: 'Monthly Invoice',
  PAY: 'Payment',
  JIN: 'Job Invoice',
  REM: 'Remittance',
  RIM: 'Remittance Invoice',
  CHG: 'Charge',
  ADJ: 'Adjustment',
  WKI: 'Weekly Invoice',
  LSS: 'Loss',
  EXP: 'Expense',
  RRM: 'Reversed Remittance',
  RPY: 'Reversed Payment',
  RRI: 'Reversed Remittance Invoice',
  RCG: 'Reversed Charge',
  RAD: 'Reversed Adjustment',
  RJI: 'Reversed Job Invoice',
  RMI: 'Reversed Monthly Invoice',
  RWK: 'Reversed Weekly Invoice',
  RLS: 'Reversed Loss',
  REX: 'Reversed Expense',
  REV: 'Reversal',
};

const statusBadge = (statusName) => {
  const s = (statusName || '').toLowerCase();
  if (s === 'active')   return { bg: '#dcfce7', color: '#16a34a', label: statusName };
  if (s === 'inactive') return { bg: '#fef9c3', color: '#a16207', label: statusName };
  if (!statusName)      return { bg: '#f3f4f6', color: '#6b7280', label: 'Unknown' };
  return { bg: '#fee2e2', color: '#dc2626', label: statusName };
};

const OwnerDashboard = () => {
    const { user } = useAuth();
    const ownerCode = user?.linked_entity_code;

    const [owner, setOwner] = useState(null);
    const [systemWeek, setSystemWeek] = useState(null);
    const [ownerCars, setOwnerCars] = useState([]);
    const [monthlySummary, setMonthlySummary] = useState([]);
    const [carsError, setCarsError] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!ownerCode) return;
        const load = async () => {
            setLoading(true);
            setError(null);

            const now = new Date();
            const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
            const pad = n => String(n).padStart(2, '0');
            const dateFrom = `${firstOfMonth.getFullYear()}-${pad(firstOfMonth.getMonth() + 1)}-${pad(firstOfMonth.getDate())}`;
            const dateTo   = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;

            const [ownerRes, weeksRes, stmtRes, carsRes] = await Promise.allSettled([
                axios.get(`${rootUrl}/owners/${ownerCode}`),
                axios.get(`${rootUrl}/api/system/valid_weeks`),
                axios.get(`${rootUrl}/api/owners/${ownerCode}/statement`, {
                    params: { date_from: dateFrom, date_to: dateTo }
                }),
                axios.get(`${rootUrl}/owners/${ownerCode}/cars`),
            ]);

            if (ownerRes.status === 'fulfilled') {
                setOwner(ownerRes.value.data.data);
            } else {
                setError('Failed to load dashboard data.');
            }

            if (weeksRes.status === 'fulfilled') {
                const raw = weeksRes.value.data.system_week;
                setSystemWeek(raw ? raw.split('_')[1] : null);
            }

            if (stmtRes.status === 'fulfilled') {
                const summary = (stmtRes.value.data.summary || [])
                    .map(s => ({
                        trans_type: s.trans_type,
                        label: TYPE_LABELS[s.trans_type] || s.trans_type,
                        count: s.count,
                        net: parseFloat(s.total_debit || 0) - parseFloat(s.total_credit || 0),
                    }))
                    .sort((a, b) => Math.abs(b.net) - Math.abs(a.net));
                setMonthlySummary(summary);
            }

            if (carsRes.status === 'fulfilled') {
                setOwnerCars(carsRes.value.data.data || []);
            } else {
                setCarsError(true);
            }

            setLoading(false);
        };
        load();
    }, [ownerCode]);

    if (!ownerCode) return (
        <div className="min-h-screen flex items-center justify-center text-gray-500 pb-20">
            No owner account linked.
        </div>
    );

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center pb-20">
            <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
        </div>
    );

    const now = new Date();
    const monthLabel = now.toLocaleDateString('en-KE', { month: 'long', year: 'numeric' });

    return (
        <div className="min-h-screen bg-gray-50 pb-20">
            {/* Orange header */}
            <div className="bg-orange-500 px-4 pt-10 pb-8">
                <p className="text-orange-100 text-sm">Welcome back,</p>
                <h1 className="text-white text-2xl font-bold mt-0.5">{owner?.name || user?.name}</h1>
                <p className="text-orange-100 text-sm mt-1">Code: {ownerCode}</p>
            </div>

            <div className="px-4 -mt-4 space-y-3">
                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2 text-red-600 text-sm">
                        <HiExclamationCircle className="w-4 h-4 flex-shrink-0" />
                        {error}
                    </div>
                )}

                {/* Period + Balance card */}
                {!error && (
                    <div className="bg-white rounded-2xl shadow-sm p-4 flex items-center justify-between">
                        <div>
                            <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold">Period</p>
                            <p className="font-bold text-gray-800 mt-0.5">{monthLabel}</p>
                            {systemWeek && (
                                <p className="text-xs text-gray-400 mt-0.5">Week {systemWeek}</p>
                            )}
                        </div>
                        <div className="text-right">
                            <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold">Balance</p>
                            <p className="font-extrabold text-orange-500 text-lg mt-0.5">{formatMoney(owner?.bal ?? 0)}</p>
                            <p className="text-xs text-gray-400 mt-0.5">current</p>
                        </div>
                    </div>
                )}

                {/* This Month summary by type */}
                {!error && (
                    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                        <div className="px-4 py-3 border-b border-gray-50 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <HiCurrencyDollar className="w-5 h-5 text-orange-500" />
                                <h2 className="font-semibold text-gray-700 text-sm">This Month</h2>
                            </div>
                            <span className="text-xs text-gray-400">{monthLabel}</span>
                        </div>
                        {monthlySummary.length === 0 ? (
                            <p className="text-gray-400 text-sm text-center py-6">No activity this month.</p>
                        ) : (
                            <ul className="divide-y divide-gray-50">
                                {monthlySummary.map(row => (
                                    <li key={row.trans_type} className="px-4 py-3 flex items-center justify-between">
                                        <div>
                                            <p className="text-sm font-medium text-gray-800">{row.label}</p>
                                            <p className="text-xs text-gray-400 mt-0.5">
                                                {row.trans_type} · {row.count} {Number(row.count) === 1 ? 'entry' : 'entries'}
                                            </p>
                                        </div>
                                        <span className={`text-sm font-semibold ${row.net >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                                            {row.net >= 0 ? '+' : ''}{formatMoney(row.net)}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}

                {/* My Cars */}
                {!error && (
                    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                        <div className="px-4 py-3 border-b border-gray-50 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <HiTruck className="w-5 h-5 text-orange-500" />
                                <h2 className="font-semibold text-gray-700 text-sm">
                                    My Cars {ownerCars.length > 0 && `(${ownerCars.length})`}
                                </h2>
                            </div>
                        </div>
                        {carsError ? (
                            <p className="text-gray-400 text-sm text-center py-6">Unable to load cars.</p>
                        ) : ownerCars.length === 0 ? (
                            <p className="text-gray-400 text-sm text-center py-6">No cars on this account.</p>
                        ) : (
                            <ul className="divide-y divide-gray-50">
                                {ownerCars.map(car => {
                                    const badge = statusBadge(car.status_name);
                                    const makeModel = [car.make, car.model].filter(Boolean).join(' ');
                                    return (
                                        <li key={car.plate_no} className="px-4 py-3 flex items-center justify-between">
                                            <div>
                                                <p className="text-sm font-bold text-gray-800 tracking-wide">{car.plate_no}</p>
                                                {makeModel && (
                                                    <p className="text-xs text-gray-400 mt-0.5">{makeModel}</p>
                                                )}
                                            </div>
                                            <span
                                                className="text-xs font-semibold rounded-full px-3 py-1"
                                                style={{ background: badge.bg, color: badge.color }}
                                            >
                                                {badge.label}
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                )}
            </div>

            <OwnerNav />
        </div>
    );
};

export default OwnerDashboard;
